import { PAYOUT_EUROPEAN_ID } from "@faire/help-center-utils/articleIds";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalProducts from "@faire/web-api--source/routes/brand-portal/my-shop/products";
import supportArticle from "@faire/web-api--source/routes/support/articles/articleId";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const InternationalPricingIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="Providing specific pricing for multiple currencies offers a more consistent experience for your retailers around the world. You can toggle <BoldFormatting>unique pricing per currency</BoldFormatting> for each country (or group of countries, like Europe) that your shop sells to."
          values={{
            BoldFormatting,
          }}
          description={{
            text: "Introduction to international pricing",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="To offer currency-specific pricing on a product, follow these steps:"
          description={{
            text: "Header for steps to set international pricing",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Go to your <LinkFormatting>Products</LinkFormatting> page and select the product to edit."
              values={{
                LinkFormatting: ProductsLinkFormatting,
              }}
              description={{
                text: "First step to set international pricing",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Find the <BoldFormatting>International pricing</BoldFormatting> dropdown, and select <BoldFormatting>Set unique pricing per currency</BoldFormatting>."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Second step to set international pricing",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Enter the new prices for each country in local currency."
              description={{
                text: "Third step to set international pricing",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>Save</BoldFormatting> all changes."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fourth step to set international pricing",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Note that new pricing won’t affect any orders that have already been placed."
          description={{
            text: "Note about pricing changes",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="All payouts on Faire will be converted to your payout currency. For example, if your brand is based in the US and a retailer in the UK pays £10 for an order, that amount will be converted to USD based on the daily exchange rate on the date the order was placed."
          description={{
            text: "Explanation of payout conversion",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="European brands can learn more about payout conversions in this article on <LinkFormatting>European brand payouts</LinkFormatting>."
          values={{
            LinkFormatting: ArticleLinkFormatting,
          }}
          description={{
            text: "Reference to related article",
          }}
        />
      </Typography>
    </Column>
  );
};

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const ArticleLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={supportArticle(PAYOUT_EUROPEAN_ID)}
    target="_blank"
  >
    {chunks}
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
